import { get, isEqual } from 'lodash'
import { snakeCaseToCamelCaseKeepingSpecialCharacters } from '@helpers/StringHelper'

export const UPLOAD_MODE = 'upload'
export const TTS_MODE = 'tts'

function fieldPropFor (field) {
  return snakeCaseToCamelCaseKeepingSpecialCharacters(field)
}

function hasPersistedAudio (audio) {
  if (!audio) return false
  if (typeof audio === 'string' && audio) return true
  return Boolean(audio?.url)
}

function hasInSessionAudioFile (audio) {
  return Boolean(audio?.file)
}

function hasPlayableAudio (audio) {
  return hasPersistedAudio(audio) || hasInSessionAudioFile(audio)
}

// Same shape as CallService::PlaybackFields::FIELD_REGEXP. The name is at least
// two characters, so a one-letter marker is not a playback token.
const PLAYBACK_TOKEN = /(\[|<)([!?A-Za-z]\w+)(\]|>)/

// Public: True when the script contains a playback token the server will defer.
export function scriptHasPlaybackToken (value) {
  return Boolean(value && PLAYBACK_TOKEN.test(value))
}

// Public: Returns true when TTS mode has text that is not backed by generated/playable audio.
export function ttsAudioBlocksSave ({
  mode,
  ttsValue,
  generatedFromText,
  audioValue,
}) {
  if (mode !== TTS_MODE) return false
  if (!ttsValue?.trim()) return false

  return ttsValue !== generatedFromText || !hasPlayableAudio(audioValue)
}

// Public: Returns model field updates to enforce audio/TTS exclusivity on save.
// When neither field changed, returns null so a mode switch alone is ignored.
export function resolveAudioOrAutomatedVoiceSaveUpdates ({
  model,
  initialModel,
  mode,
  audioField,
  ttsField,
  generatedFromText = null,
}) {
  const audioFieldProp = fieldPropFor(audioField)
  const ttsFieldProp = fieldPropFor(ttsField)
  const audioValue = get(model, audioFieldProp)
  const ttsValue = get(model, ttsFieldProp)
  const initialAudio = get(initialModel, audioFieldProp)
  const initialTts = get(initialModel, ttsFieldProp)
  const audioChanged = !isEqual(initialAudio, audioValue)
  const ttsChanged = !isEqual(initialTts, ttsValue)

  if (!audioChanged && !ttsChanged) return null

  const bothChanged = ttsChanged && audioChanged

  // Only clear stale generated audio in TTS mode. Upload mode clears the script
  // (ttsValue becomes null), which must not be treated as "text changed without
  // regenerating" or the uploaded file would be wiped from the save payload.
  if (mode === TTS_MODE && bothChanged && generatedFromText != null && ttsValue !== generatedFromText) {
    return { [audioFieldProp]: null }
  }

  if (ttsValue && (bothChanged || (mode === TTS_MODE && audioChanged))) {
    return { [ttsFieldProp]: ttsValue, [audioFieldProp]: audioValue }
  }

  if (mode === TTS_MODE) {
    if (ttsChanged && !audioChanged) {
      return { [audioFieldProp]: null }
    }

    return null
  }

  if (audioChanged) {
    return { [ttsFieldProp]: null }
  }

  return null
}
